import { useTranslation } from 'react-i18next'
import AccountCard from './account-card'
import { useFormatAmount } from '../../../../../core/hooks/useFormatAmount'
import { useContext } from 'react'
import { StoreTrajectoryContext } from '../../state/store-trajectory-context'
import { useFormatNumber } from '../../../../../core/hooks/useFormatNumber'
import { Instore, Outstore } from '../../../../../core/api/trajectory-photo/useApiGetStoreForecast'
import { formatMarginRate } from './format-margin-rate'

type ChannelOmniProps = {
  instore: Instore
  outstore: Outstore
}

const computeProgression = (forecast: number | null, reference: number | null) => {
  if (forecast == null || reference == null) {
    return null
  }
  return forecast / reference - 1
}

const computeAverage = (value1: number | null, value2: number | null) => {
  if (value1 == null || value2 == null) {
    return null
  }
  return (value1 + value2) / 2
}

export const computeWeightedMarginRate = (
  instoreGmv: number | null,
  instoreMarginRate: number | null,
  outstoreGmv: number | null,
  outstoreMarginRate: number | null
) => {
  if (instoreGmv == null || instoreMarginRate == null || outstoreGmv == null || outstoreMarginRate == null) {
    return null
  }
  const totalGmv = instoreGmv + outstoreGmv
  if (totalGmv === 0) {
    return null
  }
  return (instoreGmv * instoreMarginRate + outstoreGmv * outstoreMarginRate) / totalGmv
}

const ChannelOmni = ({ instore, outstore }: ChannelOmniProps) => {
  const { t } = useTranslation()
  const formatNumber = useFormatNumber()
  const formatAmount = useFormatAmount()

  const {
    store,
    forecastInstoreGmv,
    forecastInstoreMarginRate,
    forecastInstoreSellingPrice,
    forecastInstoreQuantities,
    forecastOutstoreGmv,
    forecastOutstoreMarginRate,
    forecastOutstoreSellingPrice,
    forecastOutstoreQuantities
  } = useContext(StoreTrajectoryContext)

  const currency = store?.currency ?? ''

  const gmvForecasted = forecastInstoreGmv + forecastOutstoreGmv
  const gmvReference = instore.reference_gmv + outstore.reference_gmv
  const gmvProgression = computeProgression(gmvForecasted, gmvReference)

  const marginRateForecasted = computeWeightedMarginRate(
    forecastInstoreGmv,
    forecastInstoreMarginRate,
    forecastOutstoreGmv,
    forecastOutstoreMarginRate
  )
  const marginRateReference = computeWeightedMarginRate(
    instore.reference_gmv,
    instore.reference_margin_rate,
    outstore.reference_gmv,
    outstore.reference_margin_rate
  )
  const marginRateProgression = computeProgression(marginRateForecasted, marginRateReference)

  const sellingPriceForecasted = computeAverage(forecastInstoreSellingPrice, forecastOutstoreSellingPrice)
  const sellingPriceReference = computeAverage(instore.reference_selling_price, outstore.reference_selling_price)
  const sellingPriceProgression = computeProgression(sellingPriceForecasted, sellingPriceReference)

  const quantitiesForecasted = forecastInstoreQuantities + forecastOutstoreQuantities
  const quantitiesReference = instore.reference_quantities + outstore.reference_quantities
  const quantitiesProgression = computeProgression(quantitiesForecasted, quantitiesReference)

  return (
    <div className="mg-store-trajectory-edit-channels-omni">
      <div className="mg-channels-channel-title">
        <div className="mg-channels-channel-title-text vp-subtitle-l" data-testid="mg-channels-channel-title-text">
          {t('general.trajectory.store.edit.channels.omni.title')}
        </div>
      </div>

      <div className="mg-store-trajectory-edit-channels-omni-cards">
        <AccountCard
          title={t('general.trajectory.store.edit.channels.account.gmv.forecasted_title')}
          referenceValue={gmvForecasted}
          referenceProgression={gmvProgression}
          formatValue={value =>
            formatAmount(value, currency, '--', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
          }
        />

        <AccountCard
          title={t('general.trajectory.store.edit.channels.account.margin_rate.forecasted_title')}
          referenceValue={marginRateForecasted}
          referenceProgression={marginRateProgression}
          formatValue={value => formatMarginRate(formatNumber, value)}
        />

        <AccountCard
          title={t('general.trajectory.store.edit.channels.account.selling_price.forecasted_title')}
          referenceValue={sellingPriceForecasted}
          referenceProgression={sellingPriceProgression}
          formatValue={value =>
            formatAmount(value, currency, '--', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          }
        />

        <AccountCard
          title={t('general.trajectory.store.edit.channels.account.quantities')}
          referenceValue={quantitiesForecasted}
          referenceProgression={quantitiesProgression}
          formatValue={value => formatAmount(value, '', '--', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        />
      </div>
    </div>
  )
}

export default ChannelOmni
